import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import DataTable, { type Column } from '../DataTable/DataTable';
import { usePagination } from '../../hooks/usePagination';
import { useSorting } from '../../hooks/useSorting';
import FilterSection, { UnifiedFilter, type FilterFieldConfig } from './FilterSection';
import { convertUnifiedFiltersToLegacyFormat } from './filterUtils';
import { cn } from '../../lib/utils';
import ConfirmationDialog from '../ui/ConfirmationDialog';
import PageHeader from '../ui/PageHeader';
import TablePresets from './TablePresets';
import ListPageToolbar from './ListPageToolbar';
import useSWR from 'swr';
import { getTablePresets, getDefaultTablePreset, TablePreset } from '../../services/tablePresetsService';
import { useApiOptions } from '../../hooks/useApiOptions';
import { useNotification } from '../../contexts/NotificationContext';
import { useSettings } from '../../contexts/SettingsContext';
import { QuickFilterButtons } from '../FiltersPanel';
import { getFilterPresets, FilterPreset } from '../../services/filterPresetsService';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  getListStateStorageKey,
  loadPersistedGenericListState,
  persistGenericListState,
} from '../../lib/listStatePersistence';
import type { FilterValues, ListModuleConfig, TableActionsOptions } from './crudTypes';
import { useModuleTranslation } from '../../i18n/useModuleTranslation';
import {
  translateColumnHeader,
  translateFieldLabel,
  translateFilterField,
  translateFilterGroupLabel,
  translateToolbarDropdownItem,
  translateToolbarTitle,
} from '../../i18n/moduleT';
import { resolveCommonAction } from '../../i18n/labels';

function getErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object') {
    const err = error as { response?: { data?: { message?: string } }; message?: string };
    return err.response?.data?.message || err.message || fallback;
  }
  return fallback;
}

interface GenericListPageProps<T extends object> {
  /** Optional context for getColumns/getTableActions: row callbacks and/or scalar scope (e.g. groupId). */
  listContext?: Record<string, unknown>;
  useData: import('./crudTypes').ListDataHook<T>;
  config: ListModuleConfig<T>;
  /** Merged into unified filter state once on mount (e.g. from URL query on list pages). */
  initialUnifiedFilters?: Record<string, UnifiedFilter>;
  /** Rendered below filter panels and above the table; receives all rows matching current filters and search (before pagination). */
  renderListSummary?: (args: {
    items: T[];
    isLoading: boolean;
    searchTerm: string;
  }) => React.ReactNode;
  /** When true, disables table presets and export (compact/embedded hub mode). */
  embedded?: boolean;
}

export type { TableActionsOptions } from './crudTypes';

function GenericListPage<T extends object>({
  useData,
  config,
  listContext,
  initialUnifiedFilters,
  renderListSummary,
  embedded = false,
}: GenericListPageProps<T>) {
  const { t } = useTranslation('common');
  const i18nKey = config.module.i18nKey;
  const { mt } = useModuleTranslation(i18nKey);
  const moduleTitle = useMemo(
    () => (i18nKey ? mt('title', config.module.title) : config.module.title),
    [i18nKey, mt, config.module.title],
  );
  const moduleDescription = useMemo(() => {
    if (!config.module.description) return undefined;
    return i18nKey ? mt('description', config.module.description) : config.module.description;
  }, [config.module.description, i18nKey, mt]);
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const { appConfig } = useSettings();
  const { colors } = useTheme();
  const { can } = useAuth();
  const permissionModuleKey = config.module.permissionModuleKey;
  const canDelete = !permissionModuleKey || can(permissionModuleKey, 'delete');
  const canEdit = !permissionModuleKey || can(permissionModuleKey, 'edit');
  const tableActionsOptions: TableActionsOptions = { canDelete, canEdit };
  const compactMode = embedded ?? false;
  const enableAdvancedSearch = appConfig.features.enableAdvancedSearch && !compactMode;
  const enableExport = appConfig.features.enableExport && !compactMode;
  const enableTablePresets = appConfig.features.enableTablePresets && !compactMode;

  const defaultFilterTab = config.filterGroups?.[0]?.name || 'basic';
  const listStateStorageKey = getListStateStorageKey(config.module.route.list);
  const persistedList = loadPersistedGenericListState(listStateStorageKey, defaultFilterTab);

  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(() => persistedList?.searchTerm ?? '');
  const [filtersVisible, setFiltersVisible] = useState(() => persistedList?.filtersVisible ?? false);
  const [activeFilterTab, setActiveFilterTab] = useState<string>(
    () => persistedList?.activeFilterTab ?? defaultFilterTab
  );
  const defaultTableDensity = config.defaultTableDensity || appConfig.table.defaultDensity;
  const [tableDensity, setTableDensity] = useState<'compact' | 'normal' | 'comfortable'>(defaultTableDensity);

  // Unified filter state
  const [unifiedFilters, setUnifiedFilters] = useState<Record<string, UnifiedFilter>>(
    () => persistedList?.unifiedFilters ?? {}
  );

  const urlFiltersMergedRef = useRef(false);
  useEffect(() => {
    if (urlFiltersMergedRef.current || !initialUnifiedFilters || Object.keys(initialUnifiedFilters).length === 0) {
      return;
    }
    setUnifiedFilters((prev) => ({ ...prev, ...initialUnifiedFilters }));
    urlFiltersMergedRef.current = true;
  }, [initialUnifiedFilters]);

  // Table preset state
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>({});
  const [columnWidths, setColumnWidths] = useState<Record<string, string>>({});
  const [columnOrder, setColumnOrder] = useState<string[]>([]);
  const [columnLabels, setColumnLabels] = useState<Record<string, string>>({});
  const [footerConfig, setFooterConfig] = useState<{
    showFooter?: boolean;
    useFilteredData?: boolean;
    footerColumns?: Record<string, {
      footerType: 'sum' | 'avg' | 'count' | 'custom';
      footerLabel?: string;
    }>;
  }>({ showFooter: false, useFilteredData: true, footerColumns: {} });

  // Non-persistent UI state
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [showTablePresets, setShowTablePresets] = useState(false);
  const [tablePresetsPanelWidth, setTablePresetsPanelWidth] = useState(420);
  const [tablePresetsResizing, setTablePresetsResizing] = useState(false);
  const tablePresetsResizeStartRef = useRef({ x: 0, width: 0 });
  const [lastAppliedPreset, setLastAppliedPreset] = useState<TablePreset | null>(null);
  
  const tableId = config.module.title.toLowerCase().replace(/\s+/g, '-');

  const dataHookResult = useData(searchTerm || undefined);
  const data = (dataHookResult.data || dataHookResult.clients || []) as T[];
  const isLoading = dataHookResult.isLoading;
  const mutate = dataHookResult.mutate;

  // Convert unified filters to legacy format and apply filters
  const filteredData = useMemo(() => {
    // Get all filter keys from config to ensure all are initialized
    const allFilterKeys = config.filters.fields.map((f) => f.key);
    // Identify text filter keys (those that use string instead of array)
    const textFilterKeys = config.filters.fields
      .filter((f) => f.type === 'text')
      .map((f) => f.key);

    const { filters, dateRanges } = convertUnifiedFiltersToLegacyFormat(
      unifiedFilters,
      allFilterKeys,
      textFilterKeys
    );
    let result = config.filters.applyFilter(data, filters as FilterValues, dateRanges);
    if (config.filters.applySearch && searchTerm?.trim()) {
      result = config.filters.applySearch(result, searchTerm.trim());
    }
    return result;
  }, [data, unifiedFilters, config.filters, searchTerm]);

  // Sorting (use config.defaultSort so list can start sorted by e.g. end date ascending)
  const { sortConfig, handleSort, setSortConfig, sortedData } = useSorting<T>(config.defaultSort);

  // Apply sorting
  const sortedItems = useMemo(
    () => sortedData(filteredData),
    [filteredData, sortedData]
  );

  // Pagination - use settings if available, otherwise use config
  const { paginationProps, paginatedData } = usePagination({
    totalItems: sortedItems.length,
    initialItemsPerPage: appConfig.table.defaultPageSize, // Use settings value instead of config
  });

  // Apply pagination
  const paginatedItems = useMemo(
    () => paginatedData(sortedItems),
    [sortedItems, paginatedData]
  );

  const handleDelete = async (id: number) => {
    try {
      await config.delete.handler(id);
      mutate();
      setDeleteConfirm(null);
      showSuccess('Item deleted successfully');
    } catch (error: unknown) {
      console.error('Failed to delete:', error);
      const errorMessage = getErrorMessage(error, config.delete.errorMessage);
      showError(errorMessage);
    }
  };

  // Get all columns from config using config's formatDate
  const allColumns = useMemo(
    () => {
      const onDelete = (id: number) => setDeleteConfirm(id);
      const contextWithMutate = { ...listContext, mutate, onDelete, tableActionsOptions };
      // Try to pass colors and listContext (for functions that accept them)
      try {
        const result = config.getColumns(config.utils.formatDate, undefined, colors, contextWithMutate);
        if (result && Array.isArray(result) && result.length > 0) return result;
      } catch {}
      try {
        const result = config.getColumns(config.utils.formatDate, undefined, colors);
        if (result && Array.isArray(result) && result.length > 0) return result;
      } catch {}
      try {
        const result = config.getColumns(config.utils.formatDate, colors);
        if (result && Array.isArray(result) && result.length > 0) return result;
      } catch {}
      // Fallback to original call
      return config.getColumns(config.utils.formatDate);
    },
    [config, colors, listContext, mutate, tableActionsOptions]
  );

  const localizedAllColumns = useMemo(() => {
    if (!i18nKey) return allColumns;
    return allColumns.map((col) => ({
      ...col,
      header: translateColumnHeader(mt, String(col.key), col.header),
    }));
  }, [allColumns, i18nKey, mt]);

  const localizedFilterFields = useMemo(() => {
    if (!i18nKey) return config.filters.fields;
    return config.filters.fields.map((f) => ({
      ...f,
      ...translateFilterField(mt, f.key, f),
    }));
  }, [config.filters.fields, i18nKey, mt]);

  const localizedFilterGroups = useMemo(() => {
    if (!i18nKey || !config.filterGroups) return config.filterGroups;
    return config.filterGroups.map((g) => ({
      ...g,
      label: translateFilterGroupLabel(mt, g.name, g.label),
    }));
  }, [config.filterGroups, i18nKey, mt]);

  const localizedToolbarActions = useMemo(() => {
    if (!i18nKey) return config.toolbar.actions;
    return config.toolbar.actions.map((action) => ({
      ...action,
      title: translateToolbarTitle(mt, action.key, action.title),
      dropdown: action.dropdown?.map((item) => ({
        ...item,
        label: translateToolbarDropdownItem(mt, action.key, item.key, item.label),
      })),
    }));
  }, [config.toolbar.actions, i18nKey, mt]);

  const searchPlaceholder = useMemo(
    () => (i18nKey ? mt('searchPlaceholder', config.search.placeholder) : config.search.placeholder),
    [i18nKey, mt, config.search.placeholder],
  );

  const emptyMessage = useMemo(() => {
    if (searchTerm) {
      return i18nKey
        ? mt('emptyStates.noSearchResults', config.emptyStates.noSearchResults)
        : config.emptyStates.noSearchResults;
    }
    return i18nKey ? mt('emptyStates.noData', config.emptyStates.noData) : config.emptyStates.noData;
  }, [searchTerm, i18nKey, mt, config.emptyStates]);

  const deleteDialog = useMemo(
    () => ({
      title: i18nKey ? mt('delete.title', config.delete.title) : config.delete.title,
      message: i18nKey ? mt('delete.message', config.delete.message) : config.delete.message,
      confirmText: i18nKey
        ? resolveCommonAction(t, mt('delete.confirmText', config.delete.confirmText))
        : resolveCommonAction(t, config.delete.confirmText),
      cancelText: i18nKey
        ? resolveCommonAction(t, mt('delete.cancelText', config.delete.cancelText))
        : resolveCommonAction(t, config.delete.cancelText),
    }),
    [i18nKey, mt, t, config.delete],
  );

  // Collect all API endpoints from filter fields and form fields that need to fetch options
  // Priority: form fields (config.fields) first, then filter fields (config.filters.fields)
  const apiEndpoints = useMemo(() => {
    const endpoints: Record<string, { endpoint: string; labelField?: string }> = {};
    
    // First, collect from form fields (config.fields)
    config.fields.forEach(field => {
      if (field.apiEndpoint) {
        endpoints[field.name] = {
          endpoint: field.apiEndpoint,
          labelField: field.labelField,
        };
      }
    });
    
    // Then, collect from filter fields (config.filters.fields) as fallback
    // Only add if not already present (form fields take priority)
    config.filters.fields.forEach(field => {
      if (field.apiEndpoint && !endpoints[field.key]) {
        endpoints[field.key] = {
          endpoint: field.apiEndpoint,
          labelField: field.labelField,
        };
      }
    });
    
    return endpoints;
  }, [config.filters.fields, config.fields]);

  // Fetch options from API endpoints using custom hook
  const apiOptionsData = useApiOptions(apiEndpoints);

  // Load filter presets to display as quick filters
  const { data: savedFilterPresets = [] } = useSWR(
    enableAdvancedSearch ? `filter-presets-${tableId}` : null,
    enableAdvancedSearch ? () => getFilterPresets(tableId) : null,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
    }
  );

  // Restore quick filter from URL when returning from add/edit (so the selected filter is preserved)
  const restoredPresetRef = useRef<string | null>(null);
  useEffect(() => {
    if (!enableAdvancedSearch || !savedFilterPresets?.length) return;
    const presetId = searchParams.get('preset');
    if (!presetId || presetId === 'all') {
      restoredPresetRef.current = null;
      return;
    }
    if (restoredPresetRef.current === presetId) return;
    const preset = savedFilterPresets.find((p: FilterPreset) => String(p.id) === presetId);
    if (preset?.filters && typeof preset.filters === 'object') {
      setUnifiedFilters({ ...(preset.filters as Record<string, UnifiedFilter>) });
      restoredPresetRef.current = presetId;
    }
  }, [enableAdvancedSearch, savedFilterPresets, searchParams]);

  // Keep search + filter UI in sessionStorage so navigating to edit/detail and back preserves them
  useEffect(() => {
    persistGenericListState(listStateStorageKey, {
      searchTerm,
      unifiedFilters,
      activeFilterTab,
      filtersVisible,
    });
  }, [listStateStorageKey, searchTerm, unifiedFilters, activeFilterTab, filtersVisible]);

  // Initialize visible columns on mount
  useEffect(() => {
    if (Object.keys(visibleColumns).length === 0 && allColumns.length > 0) {
      const initialVisibleColumns = allColumns.reduce(
        (acc, col) => ({
          ...acc,
          [String(col.key)]: true,
        }),
        {}
      );
      setVisibleColumns(initialVisibleColumns);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allColumns.length]);

  // Filter columns based on visibility and apply footer, sizing & order config
  const columns = useMemo(() => {
    let filtered = localizedAllColumns;
    if (Object.keys(visibleColumns).length > 0) {
      filtered = localizedAllColumns.filter((col) => (visibleColumns as Record<string, boolean>)[String(col.key)] !== false);
    }

    // Apply footer configuration, max-width, and custom column labels from presets
    let mapped = filtered.map((col) => {
      const columnKey = String(col.key);
      const footerColumnConfig = (footerConfig?.footerColumns as Record<string, { footerType?: string; footerLabel?: string }> | undefined)?.[columnKey];
      const maxWidthFromPreset = columnWidths[columnKey];
      const customLabel = columnLabels[columnKey];

      const updated: Column<T> = {
        ...col,
        header: customLabel || col.header || String(col.key),
      };

      if (footerColumnConfig) {
        updated.footerType = footerColumnConfig.footerType as Column<T>['footerType'];
        updated.footerLabel = footerColumnConfig.footerLabel;
      }

      if (maxWidthFromPreset) {
        updated.maxWidth = maxWidthFromPreset;
      }

      return updated;
    });

    // Apply column order from presets (if any)
    if (columnOrder && columnOrder.length > 0) {
      const orderMap = new Map<string, number>();
      columnOrder.forEach((key, index) => {
        orderMap.set(String(key), index);
      });

      mapped = mapped
        .slice()
        .sort((a, b) => {
          const aKey = String(a.key);
          const bKey = String(b.key);
          const aIndex = orderMap.has(aKey) ? (orderMap.get(aKey) as number) : Number.MAX_SAFE_INTEGER;
          const bIndex = orderMap.has(bKey) ? (orderMap.get(bKey) as number) : Number.MAX_SAFE_INTEGER;
          if (aIndex !== bIndex) return aIndex - bIndex;
          // Fallback to original order when both are not in columnOrder
          const originalAIndex = localizedAllColumns.findIndex((col) => String(col.key) === aKey);
          const originalBIndex = localizedAllColumns.findIndex((col) => String(col.key) === bKey);
          return originalAIndex - originalBIndex;
        });
    }

    return mapped;
  }, [localizedAllColumns, visibleColumns, footerConfig, columnWidths, columnOrder, columnLabels]);

  // Load table presets from API using SWR
  const { data: savedTablePresets = [], mutate: mutateTablePresets } = useSWR(
    enableTablePresets ? `table-presets-${tableId}` : null,
    enableTablePresets ? () => getTablePresets(tableId) : null,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
    }
  );

  // Resize table presets panel (sizebar on left edge)
  useEffect(() => {
    if (!tablePresetsResizing) return;
    const minW = 320;
    const maxW = 720;
    const onMove = (e: MouseEvent) => {
      const start = tablePresetsResizeStartRef.current;
      const newW = start.width + start.x - e.clientX;
      setTablePresetsPanelWidth(Math.min(maxW, Math.max(minW, newW)));
    };
    const onUp = () => setTablePresetsResizing(false);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [tablePresetsResizing]);

  // Get current table configuration
  const getCurrentTableConfig = () => ({
    visibleColumns: visibleColumns,
    tableDensity: tableDensity,
    columnWidths: columnWidths,
    columnOrder: columnOrder || [],
    sortConfig: sortConfig
      ? [
          {
            key: sortConfig.key,
            direction: sortConfig.direction,
            priority: 1,
          },
        ]
      : [],
    footerConfig: footerConfig,
  });

  // Load default table preset on mount
  useEffect(() => {
    const loadDefaultPreset = async () => {
      try {
        const defaultPreset = await getDefaultTablePreset(tableId);
        if (defaultPreset) {
          handleLoadDefaultPreset(defaultPreset.config);
        }
      } catch (error) {
        console.warn('Failed to load default table preset:', error);
      }
    };
    if (enableTablePresets) {
      loadDefaultPreset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableId, enableTablePresets]);

  // Handle default preset loading
  const handleLoadDefaultPreset = (presetConfig: {
    visibleColumns: Record<string, boolean>;
    tableDensity: 'compact' | 'normal' | 'comfortable';
    columnWidths?: Record<string, string>;
    columnOrder?: string[];
    columnLabels?: Record<string, string>;
    sortConfig?: Array<{ key: string; direction: 'asc' | 'desc'; priority?: number }>;
    footerConfig?: {
      showFooter?: boolean;
      useFilteredData?: boolean;
      footerColumns?: Record<string, {
        footerType: 'sum' | 'avg' | 'count' | 'custom';
        footerLabel?: string;
      }>;
    };
  }) => {
    if (presetConfig.visibleColumns) {
      setVisibleColumns(presetConfig.visibleColumns);
    }
    if (presetConfig.tableDensity) {
      setTableDensity(presetConfig.tableDensity);
    }
    if (presetConfig.columnWidths) {
      setColumnWidths(presetConfig.columnWidths);
    }
    if (presetConfig.columnOrder) {
      setColumnOrder(presetConfig.columnOrder);
    }
    if (presetConfig.columnLabels !== undefined) {
      setColumnLabels(presetConfig.columnLabels);
    }
    if (presetConfig.sortConfig && presetConfig.sortConfig.length > 0) {
      const first = presetConfig.sortConfig[0];
      setSortConfig({ key: first.key, direction: first.direction });
    } else {
      setSortConfig(null);
    }
    if (presetConfig.footerConfig) {
      setFooterConfig(presetConfig.footerConfig);
    }
  };

  // Get FilterSection render output
  const filterSection = FilterSection({
    config: {
      filterFields: localizedFilterFields.map((f) => ({
        ...f,
        getValue: f.getValue
          ? (item: T) => {
              const v = f.getValue!(item);
              return v == null ? undefined : String(v);
            }
          : (item: T) => String((item as Record<string, unknown>)[f.key] ?? ''),
      })) as FilterFieldConfig[],
      formFields: config.fields.map((f) => ({
        name: f.name,
        label: i18nKey ? translateFieldLabel(mt, f.name, f.label) : f.label,
        type: f.type || 'text',
        options: f.options,
      })),
      filterGroups: localizedFilterGroups,
      applyFilter: config.filters.applyFilter,
    },
    entityName: moduleTitle,
    data: data,
    apiOptionsData: apiOptionsData, // Pass API-fetched options to filters
    filtersVisible,
    setFiltersVisible,
    unifiedFilters,
    setUnifiedFilters,
    activeFilterTab,
    setActiveFilterTab,
    searchTerm,
    setSearchTerm,
    searchPlaceholder,
    filteredCount: filteredData.length,
    enableAdvancedSearch,
  });

  return (
    <div className="flex h-full min-h-0">
      <div className="flex-1 min-w-0 flex flex-col min-h-0 overflow-hidden">
    <div className="flex flex-col h-full min-h-0">
      {moduleDescription ? (
        <PageHeader title={moduleTitle} description={moduleDescription} className="mb-3" />
      ) : null}
      <div className={cn('flex flex-col flex-1 min-h-0 rounded-xl border shadow-sm overflow-hidden', colors.border, colors.surface)}>
      {/* Top Controls (Search + Filter Toggle) */}
      <div className="flex-shrink-0 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 p-3 sm:p-4 pb-2">
        <div className="w-full sm:flex-1 min-w-0">
          {filterSection?.topControls}
        </div>
        <ListPageToolbar
          config={config}
          localizedToolbarActions={localizedToolbarActions}
          colors={colors}
          canEdit={canEdit}
          permissionModuleKey={permissionModuleKey}
          enableExport={enableExport}
          enableTablePresets={enableTablePresets}
          embedded={compactMode}
          moduleTitle={moduleTitle}
          data={data}
          sortedItems={sortedItems}
          localizedAllColumns={localizedAllColumns}
          showTablePresets={showTablePresets}
          setShowTablePresets={setShowTablePresets}
          showError={showError}
          t={t}
        />
      </div>

      {/* Quick Filter Buttons - Display saved filter presets as quick filters - Only show if advanced search is enabled */}
      {enableAdvancedSearch && (() => {
        const quickFilterPresets = savedFilterPresets.filter((preset: FilterPreset) => preset.showInQuickFilters);
        if (quickFilterPresets.length === 0) return null;

        // Always add "All" as the first filter when there are any quick filter presets
        const quickFilters = [
          {
            id: 'all',
            label: 'All',
            description: 'Show all items',
            filters: {},
            variant: 'default' as const,
          },
          ...quickFilterPresets.map((preset: FilterPreset) => ({
            id: preset.id.toString(),
            label: preset.name,
            description: preset.description,
            filters: preset.filters,
            variant: preset.isDefault ? ('primary' as const) : ('default' as const),
          })),
        ];

        return (
          <div className="flex-shrink-0 mb-1 px-3 sm:px-4">
            <QuickFilterButtons
              quickFilters={quickFilters}
              currentFilters={unifiedFilters}
              onApplyFilters={(filters) => {
                setUnifiedFilters(() => ({ ...filters }));
              }}
              onQuickFilterSelect={(quickFilterId) => {
                setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);
                  if (quickFilterId === 'all') {
                    next.delete('preset');
                  } else {
                    next.set('preset', quickFilterId);
                  }
                  return next;
                });
              }}
              layout="pills"
            />
          </div>
        );
      })()}

      {/* Filter Panels */}
      <div className="flex-shrink-0 mb-1 px-3 sm:px-4">
        {filterSection?.filterPanels}
      </div>

      {renderListSummary?.({ items: sortedItems, isLoading, searchTerm })}

      {/* Data Table - Flexible container */}
      <div className="flex-1 min-h-0 flex flex-col px-3 sm:px-4 pb-3 sm:pb-4">
        <div className="flex-1 min-h-0 overflow-hidden">
          <DataTable
            data={paginatedItems}
            columns={columns}
            loading={isLoading}
            pagination={paginationProps}
            sorting={{
              sortConfig,
              onSort: handleSort,
            }}
            actions={config.table.actionsInColumns ? undefined : (item) => {
              const onDelete = (id: number) => setDeleteConfirm(id);
              const contextWithMutate = { ...listContext, mutate };
              let actionsContent: React.ReactNode;
              try {
                actionsContent = config.getTableActions(item, onDelete, colors, contextWithMutate, tableActionsOptions);
              } catch {
                try {
                  actionsContent = config.getTableActions(item, onDelete, colors, tableActionsOptions);
                } catch {
                  try {
                    actionsContent = config.getTableActions(item, onDelete, tableActionsOptions);
                  } catch {
                    actionsContent = config.getTableActions(item, onDelete, undefined, undefined, tableActionsOptions);
                  }
                }
              }
              const getClientId = (config.table as { getClientId?: (item: T) => number | null | undefined }).getClientId;
              const clientId = getClientId?.(item);
              if (typeof clientId === 'number') {
                return (
                  <>
                    <Link
                      to={`/clients/${clientId}/summary`}
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-amber-600 hover:text-amber-900 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-900/30 transition-colors"
                      title="Organization overview"
                      aria-label="Organization overview"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </Link>
                    {actionsContent}
                  </>
                );
              }
              return actionsContent;
            }}
            actionsPosition={config.table.actionsPosition}
            emptyMessage={emptyMessage}
            onRowClick={config.table.onRowClick}
            keyExtractor={config.table.keyExtractor}
            density={tableDensity}
            showFooter={footerConfig?.showFooter || false}
            footerData={(footerConfig?.useFilteredData ?? true) ? sortedItems : data} // Use filtered data if enabled, otherwise all data
          />
        </div>
      </div>

      </div>

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <ConfirmationDialog
          isOpen={!!deleteConfirm}
          onClose={() => setDeleteConfirm(null)}
          onConfirm={() => handleDelete(deleteConfirm)}
          title={deleteDialog.title}
          message={deleteDialog.message}
          confirmText={deleteDialog.confirmText}
          cancelText={deleteDialog.cancelText}
          confirmVariant="danger"
        />
      )}
    </div>
    </div>
    {enableTablePresets && showTablePresets && (
      <div
        className={cn('flex-shrink-0 flex flex-col min-h-0 relative', colors.border)}
        style={{ width: tablePresetsPanelWidth }}
      >
        {/* Resize handle (sizebar) on the left edge */}
        <div
          data-table-presets-resize
          role="separator"
          aria-orientation="vertical"
          aria-valuenow={tablePresetsPanelWidth}
          onMouseDown={(e) => {
            if (e.button !== 0) return;
            tablePresetsResizeStartRef.current = { x: e.clientX, width: tablePresetsPanelWidth };
            setTablePresetsResizing(true);
          }}
          className={cn(
            'absolute left-0 top-0 bottom-0 w-2 -ml-px z-10 cursor-col-resize flex items-center justify-center group',
            'hover:bg-indigo-500/20 active:bg-indigo-500/30',
            tablePresetsResizing && 'bg-indigo-500/30'
          )}
          title="Drag to resize panel"
        >
          <div className="w-0.5 h-12 rounded-full bg-gray-400 dark:bg-gray-500 opacity-60 group-hover:opacity-100" />
        </div>
        <div className={cn('flex flex-col flex-1 min-h-0 border-l', colors.border)}>
        <TablePresets
          visibleColumns={visibleColumns}
          onApplyVisibleColumns={setVisibleColumns}
          allColumns={localizedAllColumns.map((col) => ({ key: String(col.key), header: col.header ?? String(col.key) }))}
          tableDensity={tableDensity}
          onApplyDensity={setTableDensity}
          columnWidths={columnWidths}
          columnOrder={columnOrder}
          columnLabels={columnLabels}
          onApplyColumnLabels={setColumnLabels}
          sortConfig={sortConfig ? [{ key: sortConfig.key, direction: sortConfig.direction, priority: 1 }] : []}
          onApplyTableConfig={(tableConfig) => {
            if (tableConfig.columnWidths) setColumnWidths(tableConfig.columnWidths);
            if (tableConfig.columnOrder) setColumnOrder(tableConfig.columnOrder);
            if (tableConfig.sortConfig && tableConfig.sortConfig.length > 0) {
              const first = tableConfig.sortConfig[0];
              setSortConfig({ key: first.key, direction: first.direction });
            } else setSortConfig(null);
          }}
          footerConfig={footerConfig}
          onApplyFooterConfig={setFooterConfig}
          tableId={tableId}
          entityName={moduleTitle}
          isOpen
          asPanel
          lastAppliedPreset={lastAppliedPreset}
          onPresetApplied={setLastAppliedPreset}
          onPresetCleared={() => setLastAppliedPreset(null)}
          onClose={() => setShowTablePresets(false)}
          onLoadDefaultPreset={handleLoadDefaultPreset}
          onPresetsChange={() => mutateTablePresets()}
        />
        </div>
      </div>
    )}
    </div>
  );
}

export default GenericListPage;

